import { useCallback, useEffect, useMemo, useState } from 'react';

import noop from 'lodash/noop';

import {
  useAccountGet,
  useApi,
  useSupportEnterFacility,
  useSupportFacilityOptions,
} from '@sbv-clients/data-access-api';
import { useQueryParamStore } from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  useFiltersStore,
  useParamStore,
  useScheduleStatusInitStore,
} from '@sbv-clients/shared-state';
import { OverlayLoader } from '@sbv-clients/ui-shared';
import { FunctionComponent } from '@sbv-clients/util-misc';

import {
  FacilitySwitcher,
  FacilitySwitcherOption,
} from '../top-nav/facility-switcher';

export const SupportFacilitySwitcher: FunctionComponent = () => {
  const api = useApi();

  const { data: accountData } = useAccountGet();
  const { data: facilities, isLoading: isLoadingFacilities } =
    useSupportFacilityOptions();
  const { mutateAsync: enterFacility } = useSupportEnterFacility();
  const { onEnterFacility } = useAuth();
  const paramStore = useParamStore();
  const scheduleStatusInitStore = useScheduleStatusInitStore();

  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [isSwitching, setIsSwitching] = useState(false);

  const facilityOptions = useMemo<FacilitySwitcherOption[]>(
    () =>
      (facilities ?? []).map((f) => ({
        value: f.id as string,
        label: f.name ?? '',
      })),
    [facilities]
  );

  useEffect(() => {
    if (accountData?.tokenFacilityId) {
      setSelectedFacilityId(accountData.tokenFacilityId);
    }
  }, [accountData?.tokenFacilityId]);

  const handleChange = useCallback(
    async (nextFacilityId: string) => {
      if (!nextFacilityId || nextFacilityId === accountData?.tokenFacilityId) {
        return;
      }

      const previousFacilityId = selectedFacilityId;
      try {
        setSelectedFacilityId(nextFacilityId);
        setIsSwitching(true);

        // Release any open employee lock before switching facility.
        await api.employeeV2Api.apiV2EmployeesLocksMineDelete().catch(noop);

        const result = await enterFacility(nextFacilityId);
        if (result?.token && result?.refreshToken) {
          // onEnterFacility stores the new tokens WITHOUT writing a new
          // session stamp so the facility switch is not misread as a
          // Refresh Configuration event.
          onEnterFacility(result.token, result.refreshToken);
          paramStore.clearAllPages();
          scheduleStatusInitStore.reset();
          useQueryParamStore.getState().resetAll();
          useFiltersStore.getState().resetAll();
          window.location.replace('/');
        }
      } catch {
        setSelectedFacilityId(previousFacilityId);
      } finally {
        setIsSwitching(false);
      }
    },
    [
      accountData?.tokenFacilityId,
      api.employeeV2Api,
      enterFacility,
      onEnterFacility,
      paramStore,
      scheduleStatusInitStore,
      selectedFacilityId,
    ]
  );

  if (isLoadingFacilities || !facilityOptions.length) return null;

  const current = facilityOptions.find((o) => o.value === selectedFacilityId);

  return (
    <>
      {isSwitching && <OverlayLoader />}
      <FacilitySwitcher
        label="Facility"
        current={current?.label ?? 'Choose a facility'}
        options={facilityOptions}
        selected={selectedFacilityId}
        onSelect={handleChange}
        disabled={isSwitching}
      />
    </>
  );
};
